import React, { useState, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { TextField, Stack, Button, IconButton, Alert } from "@mui/material";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { yupResolver } from '@hookform/resolvers/yup';

import ClientSelection from './ClientSelection';
import { validationSchema } from '../../../schemas/emailMessaging';
import useCustomMutate from '../../../hooks/useMutate';
import { clientMessaging } from '../../../sevices/clientApi';
import InputFileUpload from '../../../components/field/InputMultiFiles';
import DeleteIcone from '../../../styles/svg/delete-icon.svg';
import ConfirmationDialog from '../../../components/Dialog/ConfirmationDialog';
import ThankYou from './ThankYou';
import PrimaryButton from '../../../components/Button/Button';

function Form() {
    
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [thankYouMessage, setThankYouMessage] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    register,
    getValues,
    setValue,
    reset,
    trigger,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      client_ids: [],
      subject: "",
      body: "",
      attachments: [],
    },
  });
const { mutate, isPending } = useCustomMutate(clientMessaging, [], (data) => {
  reset();
  setSelectedFiles([]);
  setRecipients([]);
  setThankYouMessage(data.message);
},()=>{},false);

  const onSubmit = (data) => {
    const formData = new FormData();
    formData.append("body", data.body);
    formData.append("subject", data.subject);
    formData.append("client_ids", data.client_ids);

    data.attachments.forEach((file) => {
      formData.append("attachments", file);
    });
    mutate(formData);
  };

   const handleSelectedFiles = useCallback(
     (event) => {
       let filesArray = Object.values(event?.target?.files || {});

       // Filter out already selected files
       filesArray = filesArray.filter((file) => {
         return !selectedFiles.some(
           (selectedFile) => selectedFile.name === file.name
         );
       });

       // Merge the new filtered files with the existing selected files
       setSelectedFiles((prevSelectedFiles) => [
         ...prevSelectedFiles,
         ...filesArray,
       ]);
       setValue("attachments", [...selectedFiles, ...filesArray]);
     },
     [selectedFiles]
   );

   const handleDeleteFile = useCallback(
     (filename) => {
       setSelectedFiles((prevSelectedFiles) => {
         const filteredFiles = prevSelectedFiles?.filter(
           (file) => file.name !== filename
         );
         setValue("attachments", filteredFiles);
         
         // Trigger validation for attachments field
         trigger("attachments").then(() => {
           console.log("Validation triggered after file removal");
         });
         
         return filteredFiles;
       });
     },
     [selectedFiles, setValue, trigger]
   );

  return (
    <>
      {thankYouMessage && (
        <ThankYou
          message={thankYouMessage}
          open={thankYouMessage}
          handleClose={() => setThankYouMessage(false)}
        />
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3} className="form-stack">
          <ClientSelection
            control={control}
            errors={errors}
            recipients={recipients}
            setRecipients={setRecipients}
          />
          <Controller
            name="subject"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Subject"
                variant="outlined"
                fullWidth
                error={!!errors.subject}
                helperText={errors?.subject ? errors.subject.message : ""}
              />
            )}
          />
          <Controller
            name="body"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <div>
                <ReactQuill
                  {...field}
                  onChange={field.onChange}
                  placeholder="Write your message here..."
                  className="quill-editor"
                />
                {errors.body && (
                  <span className="error-message">
                    {errors?.body?.message || ''}
                  </span>
                )}
              </div>
            )}
          />
          <div>
            <p className="text label-text">Attachments</p>
            <InputFileUpload
              watch={watch}
              register={register}
              getValues={getValues}
              name={"attachments"}
              fileChange={handleSelectedFiles}
            />
            {errors?.attachments && (
              <span className="file-error">
                {errors?.attachments?.message || ''}
              </span>
            )}

            <ul className="files-list">
              {selectedFiles?.length > 0 &&
                selectedFiles?.map((file, index) => (
                  <div key={`${file?.name}-${index}`}>
                    <li
                      key={`${file?.name}-${index}`}
                      className="d-flex justify-content-space-between text"
                    >
                      {file?.name}
                      <IconButton onClick={() => handleDeleteFile(file?.name)}>
                        <img
                          src={DeleteIcone}
                          alt="delete"
                          height="24px"
                          width="24px"
                        />
                      </IconButton>
                    </li>
                    {errors?.attachments?.[index] && (
                      <span className="file-error">
                        {errors?.attachments?.[index]?.message || ''}
                      </span>
                    )}
                  </div>
                ))}
            </ul>
          </div>
          <PrimaryButton
            type="submit"
            variant="contained"
            color="primary"
            className="send-button"
            disabled={isPending}
          >
            {isPending ? "Sending..." : "Send"}
          </PrimaryButton>
        </Stack>
      </form>
    </>
  );
}

export default Form;